import React, { useState } from 'react';
import { supabase } from './supabaseClient'; 

export default function RegisterPage({ onAuthSuccess, onBackToDashboard, viewMode }) {
  const [isSignUpMode, setIsSignUpMode] = useState(false); 
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [promoCode, setPromoCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [newPassInput, setNewPassInput] = useState('');

  const handleAuthSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (isSignUpMode) {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { promo_code: promoCode ? promoCode.trim().toUpperCase() : null } }
      });
      setLoading(false);
      if (error) {
        setMessage(`❌ Error: ${error.message}`);
      } else {
        setMessage('🎯 Registration Successful! Please check your email inbox to verify your account.');
        if (data?.user) setTimeout(() => onAuthSuccess(data.user), 1500);
      }
    } else {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      setLoading(false);
      if (error) {
        setMessage(`❌ Login Failed: ${error.message}`);
      } else {
        setMessage('🔓 Access Granted. Loading Dashboard Control Hub...');
        if (data?.user) {
          // 🔍 MAKE SURE THIS WHOLE BLOCK IS HERE!
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('is_subscribed')
            .eq('email', email)
            .single();

          if (profileError) {
            console.error("Profile fetch warning:", profileError.message);
          }
          const userWithProfile = {
            ...data.user,
            is_subscribed: profileData?.is_subscribed || false
          };

          // Passes the user information AND their subscription value to App.js
          setTimeout(() => onAuthSuccess(userWithProfile), 1000);
        }
      }
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setMessage('❌ Please type your email address into the input field first.');
      return;
    }
    setLoading(true);
    setMessage('');

    // 🌐 AUTOMATIC REDIRECT DETECTOR:
    // If testing on computer, uses localhost. If live, uses mytdmcalculator.com!
    const targetRedirectUrl = window.location.origin.includes('localhost')
      ? 'https://mytdmcalculator.com' // <-- Forces emails requested anywhere to go to your real live site!
      : window.location.origin;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: targetRedirectUrl, // ⬅️ Change this from window.location.origin to targetRedirectUrl!
    });

    setLoading(false);
    if (error) {
      setMessage(`❌ Reset Error: ${error.message}`);
    } else {
      setMessage('📨 Password reset link sent! Check your email inbox.');
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!newPassInput.trim()) {
      setMessage('❌ Password input field cannot be blank.');
      return;
    }
    setLoading(true);
    setMessage('');

    const { error } = await supabase.auth.updateUser({
      password: newPassInput
    });

    setLoading(false);
    if (error) {
      alert(`❌ Update Failed: ${error.message}`);
    } else {
      alert("✅ Password successfully updated! Your profile is secure. Please log in with your new credentials.");
      onAuthSuccess(); 
    }
  };
  
  return (
    <div style={{ maxWidth: '400px', margin: '40px auto', padding: '25px', background: '#1c2226', borderRadius: '10px', border: '1px solid #2c3539', color: '#fff', fontFamily: 'system-ui', boxSizing: 'border-box' }}>
      <button type="button" onClick={onBackToDashboard} style={{ background: 'none', border: 'none', color: '#95a5a6', fontSize: '0.75rem', cursor: 'pointer', padding: '0', marginBottom: '15px', textDecoration: 'underline' }}>
        ← Back to Free Calculators
      </button>

      {viewMode === "recovery" ? (
        /* =====================================================================
           🔑 RECOVERY MODE: ONLY DISPLAY NEW PASSWORD FIELD
           ===================================================================== */
        <div>
          <h3 style={{ margin: '0 0 5px 0', color: '#2ecc71', fontSize: '1.4rem', fontWeight: '800' }}>
            UPDATE PASSWORD
          </h3>
          <p style={{ color: '#95a5a6', fontSize: '0.75rem', marginTop: '0', marginBottom: '20px', lineHeight: '1.4' }}>
            Configure your new master passkey baseline sequence below.
          </p>
          
          <form onSubmit={handleUpdatePassword} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px' }}>NEW SECURE PASSWORD:</label>
              <input 
                type="password"
                required
                placeholder="Type your new secure password"
                value={newPassInput}
                onChange={(e) => setNewPassInput(e.target.value)}
                style={{ width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', boxSizing: 'border-box', outline: 'none' }}
              />
            </div>
            <button type="submit" disabled={loading} style={{ padding: '12px', background: '#2ecc71', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 'bold', cursor: 'pointer', marginTop: '5px' }}>
              {loading ? 'Processing...' : 'Lock In New Passkey'}
            </button>
          </form>
        </div>
      ) : (
        /* =====================================================================
           👤 STANDARD MODE: DISPLAY LOGIN / REGISTRATION BLOCKS
           ===================================================================== */
        <div>
          <h3 style={{ margin: '0 0 5px 0', color: '#2ecc71', fontSize: '1.4rem', fontWeight: '800' }}>
            {isSignUpMode ? 'CREATE AN ACCOUNT' : 'SIGN IN'}
          </h3>
          <p style={{ color: '#95a5a6', fontSize: '0.75rem', marginTop: '0', marginBottom: '20px' }}>
            {isSignUpMode ? 'Unlock all 13 precision clinical pharmacokinetic suites instantly.' : 'Enter your credentials to access the premium TDM workspace.'}
          </p>
          
          <form onSubmit={handleAuthSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px' }}>EMAIL ADDRESS:</label>
              <input type="email" required placeholder="name@hospital.com" value={email} onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#95a5a6', marginBottom: '6px' }}>PASSWORD:</label>
                <button 
                  type="button" 
                  onClick={handleForgotPassword}
                  style={{ background: 'none', border: 'none', color: '#e67e22', fontSize: '0.7rem', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}
                >
                  Forgot Password?
                </button>
              </div>
              <input type="password" required placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} style={{ width: '100%', padding: '10px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', boxSizing: 'border-box', outline: 'none' }} />
            </div>
            
            {isSignUpMode && (
              <div style={{ padding: '12px', background: 'rgba(155, 89, 182, 0.05)', borderRadius: '6px', border: '1px dashed rgba(155, 89, 182, 0.3)' }}>
                <label style={{ display: 'block', fontSize: '0.68rem', fontWeight: 'bold', color: '#d6a2e8', marginBottom: '6px' }}>COLLEAGUE PROMO CODE (OPTIONAL):</label>
                <input type="text" placeholder="e.g. HOSP77" value={promoCode} onChange={(e) => setPromoCode(e.target.value)} style={{ width: '100%', padding: '8px', background: '#111416', border: '1px solid #34495e', borderRadius: '6px', color: '#fff', boxSizing: 'border-box', textTransform: 'uppercase', outline: 'none' }} />
              </div>
            )}

            <button type="submit" disabled={loading} style={{ padding: '12px', background: '#2ecc71', border: 'none', borderRadius: '6px', color: '#fff', fontWeight: 'bold', cursor: 'pointer', marginTop: '5px' }}>
              {loading ? 'Authenticating...' : isSignUpMode ? 'Sign Up & Activate Dashboard' : 'Log In to Dashboard'}
            </button>
          </form>

          <div style={{ textAlign: 'center', marginTop: '20px', borderTop: '1px solid #2c3539', paddingTop: '15px' }}>
            <button 
              type="button" 
              onClick={() => { setIsSignUpMode(!isSignUpMode); setMessage(''); }} 
              style={{ background: 'none', border: 'none', color: '#3498db', fontSize: '0.8rem', cursor: 'pointer', textDecoration: 'underline' }}
            >
              {isSignUpMode ? 'Already have an account? Sign In here' : 'Need an account? Sign Up here'}
            </button>
          </div>
        </div>
      )}
      
      {/* Dynamic Status Output Box */}
      {message && (
        <p style={{ fontSize: '0.8rem', textAlign: 'center', marginTop: '15px', color: message.includes('❌') ? '#e74c3c' : '#2ecc71', lineHeight: '1.4', marginBottom: 0 }}>
          {message}
        </p>
      )}
    </div>
  );
}